import classNames from "classnames";
import React, { Component, createRef } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import Form, { Field } from "Shared/Form";
import DollarInput from "Shared/Form/Input/DollarInput";
import { currencyAmount, required } from "Shared/Form/validators";
import styled, { css } from "styled-components";
import StepContent from "../../StepContent/StepContent";
import { BaseStepProps } from "../shared/BaseStepProps";
import { mapFieldsToValues } from "../shared/fieldValueMapper";
import FormBody from "../shared/FormBody";
import FormContainer from "../shared/FormContainer";
import FormHeader from "../shared/FormHeader";

const ColumnStyle = css`
    display: inline-block;
    padding: 10px;
    vertical-align: top;
`;

const ColumnSmall = styled("div")`
    ${ColumnStyle};
    width: calc(100% - 20px);

    ${(props) => props.theme.breakpoints.up("desktop")} {
        width: 40%;
    }
`;
const ColumnLarge = styled("div")`
    ${ColumnStyle};
    width: calc(100% - 20px);

    ${(props) => props.theme.breakpoints.up("desktop")} {
        width: 50%;
    }
`;

const StyledForm = styled(Form)`
    margin: 20px 0;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        margin: 30px 0;
    }
`;

const StepContentContainer = styled("div")`
    margin: auto;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        margin: 100px 22px 0;
    }
`;

const IncomeInformationBody = styled(FormBody)`
    margin: 15px;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        margin: 28px 20px 0 41px;
    }
`;

export interface IncomeInformationFormData {
    additionalIncome: number | null;
    additionalSource: string | null;
    primaryIncome: number | null;
}

export interface IncomeInformationProps extends BaseStepProps {
    formData: IncomeInformationFormData;
}

class IncomeInformation extends Component<IncomeInformationProps & WithTranslation> {
    private readonly fields: { [index: string]: Field };

    private fieldsPerRow = [["primaryIncome"], ["additionalSource", "additionalIncome"]];

    private incomeForm = createRef<Form>();

    constructor(props: IncomeInformationProps & WithTranslation) {
        super(props);

        this.fields = this.getFields();
    }

    public render() {
        const { isCosigner = false, className, formData = {}, t } = this.props;
        const fields = mapFieldsToValues(this.fields, formData);
        return (
            <FormContainer className={classNames("IncomeInformation", className)}>
                <FormHeader className="IncomeInformation__header">
                    {isCosigner
                        ? t("ContinueDeal.CreditApp.IncomeInformation.cosignerTitle")
                        : t("ContinueDeal.CreditApp.IncomeInformation.title")}
                </FormHeader>
                <IncomeInformationBody>
                    <StyledForm fields={fields} fieldsPerRow={this.fieldsPerRow} ref={this.incomeForm} />
                </IncomeInformationBody>
                <StepContentContainer>
                    <StepContent onBackClick={this.handleBackClick} onNextClick={this.handleNextClick} />
                </StepContentContainer>
            </FormContainer>
        );
    }

    private getFields = () => {
        const { formData, t } = this.props;
        const additionalIncome = formData && formData.additionalIncome;
        const primaryIncome = formData && formData.primaryIncome;

        return {
            additionalIncome: {
                initialValue: additionalIncome ? additionalIncome.toString() : null,
                input: DollarInput,
                label: t("ContinueDeal.CreditApp.IncomeInformation.additionalIncomeLabel"),
                validators: [
                    {
                        message: t("ContinueDeal.CreditApp.IncomeInformation.additionalIncomeInvalidMessage"),
                        validator: currencyAmount,
                    },
                ],
                wrapper: ColumnSmall,
            },
            additionalSource: {
                initialValue: formData && formData.additionalSource,
                label: t("ContinueDeal.CreditApp.IncomeInformation.additionalSourceLabel"),
                validators: [],
                wrapper: ColumnLarge,
            },
            primaryIncome: {
                initialValue: primaryIncome ? primaryIncome.toString() : null,
                input: DollarInput,
                label: t("ContinueDeal.CreditApp.IncomeInformation.primaryIncomeLabel") + "*",
                validators: [
                    {
                        message: t("ContinueDeal.CreditApp.IncomeInformation.primaryIncomeRequiredMessage"),
                        validator: required,
                    },
                    {
                        message: t("ContinueDeal.CreditApp.IncomeInformation.primaryIncomeInvalidMessage"),
                        validator: currencyAmount,
                    },
                ],
                wrapper: ColumnSmall,
            },
        };
    };

    private handleNextClick = () => {
        if (this.incomeForm.current!.isFormValid()) {
            const formData = this.incomeForm.current!.getFormData();
            return this.props.onNextClick(formData);
        }
    };

    private handleBackClick = () => {
        const formData = this.incomeForm.current!.getFormData();
        return this.props.onBackClick(formData);
    };
}

export default withTranslation()(IncomeInformation);
